import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { Link } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore, useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import Logo from '@/components/Icons/Logo';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import useRequireAuth from '@/hooks/useRequireAuth';
import ThemeSelector from '@/components/Navigation/ThemeSelector';
import InfoSelector from '@/components/Navigation/InfoSelector';

export default function SidebarHeader({ withSearch = false, isAdmin = false }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const requireAuth = useRequireAuth();
  const hasUser = useAuthStore(state => Boolean(state.user?.id));

  const handleOpenSearch = useCallback(() => {
    requireAuth(() => openModal('SearchModal'), {
      source: 'sidebar_search',
      title: t('sidebar.signInToSaveWork'),
      subtitle: t('sidebar.signInToSearchChatsSubtitle'),
      icon: 'magnifying-glass',
    });
  }, [requireAuth, openModal, t]);

  const keyboardShortcutHandlers = useMemo(() => ({
    FOCUS_SEARCH: handleOpenSearch,
  }), [handleOpenSearch]);
  useKeyboardShortcuts(keyboardShortcutHandlers);

  const handleOpenSettings = () => {
    requireAuth(
      () => openModal(isXS ? 'SettingsModalMobile' : 'SettingsModalWeb'),
      {
        source: 'sidebar_settings',
        title: t('sidebar.signInForSettings'),
        subtitle: t('sidebar.signInForSettingsSubtitle'),
        icon: 'gear',
      }
    );
  };

  const styles = StyleSheet.create({
    navbar: {
      height: 53,
      width: '100%',
      zIndex: 4,
      backgroundColor: colors.solid_color_block,
    },
    navbarInnerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      height: 53,
      width: '100%',
    },
    navbarBrand: {
      paddingLeft: 10,
    },
    brandContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    logo: {
      width: 18,
      height: 18,
      marginRight: 8,
    },
    navbarActions: {
      marginLeft: 'auto',
      flexDirection: 'row',
      alignItems: 'center',
      height: '100%',
    },
  });

  return (
    <View style={styles.navbar}>
      <View style={styles.navbarInnerContainer}>
        <View style={styles.navbarBrand}>
          <Link href="/app" asChild>
            <Pressable>
              <View style={styles.brandContainer}>
                <Logo
                  width={styles.logo.width || 40}
                  height={styles.logo.height || 40}
                  color={colors.text_color_primary}
                />
              </View>
            </Pressable>
          </Link>
        </View>

        <View style={styles.navbarActions}>
          <NavigationBarButtonContainer rightPadding>
            {withSearch && (
              <NavigationBarButton
                icon="magnifying-glass"
                title={t('navigation.search')}
                tooltip={t('navigation.search')}
                tooltipShortcut="⌘ K"
                onPress={handleOpenSearch}
              />
            )}
            {hasUser ? (
              <NavigationBarButton
                icon="gear"
                title="Settings"
                tooltip="Settings"
                tooltipShortcut="G + S"
                onPress={handleOpenSettings}
              />
            ) : (
              <>
                <ThemeSelector showAccentSelector={false} />
                <InfoSelector />
              </>
            )}
          </NavigationBarButtonContainer>
        </View>
      </View>
    </View>
  );
}
